import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home, ScrollText, Boxes, CreditCard, MoreHorizontal, ScanLine,
  ClipboardList, Package, PencilLine, BookOpen, Landmark, BarChart3,
  Megaphone, CalendarDays, Users, Wallet,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import "./mobile-navigation.css";

/* Bottom-nav gradient KONTEKSTUAL untuk layar HP (redesign 24 Agu 2026).
 *
 * Diadaptasi dari komponen 21st.dev "gradient-menu" (pilihan owner) dengan tiga
 * penyesuaian sadar:
 * 1. TSX -> JSX dan react-icons -> lucide-react (stack proyek ini; nol dependensi baru).
 * 2. Efek hover-melebar diganti ITEM AKTIF yang melebar berlabel — hover tidak
 *    ada di layar sentuh, dan bottom-nav HP butuh penanda "kamu di mana".
 * 3. KONTEKSTUAL: isi nav mengikuti modul yang sedang dibuka (keputusan owner) —
 *    di Inventory slotnya Opname/Stok/PO/Produk, di Finance Catat/Jurnal/Rekon/
 *    Laporan, dst. Slot pertama selalu Beranda sebagai jalan pulang.
 *
 * RBAC: setiap item disaring dengan logika PERSIS seperti sidebar Layout.jsx
 * (mod / mods / modPrefix + can(..., "view")) — jangan tampilkan tombol yang
 * ujungnya 403 (CLAUDE.md aturan 3). Desktop tidak tersentuh (md:hidden).
 */

const BERANDA = {
  id: "beranda", lbl: "Beranda", ikon: Home, to: "/",
  g: ["#a955ff", "#ea51ff"],
};

const KONTEKS = [
  {
    id: "stok",
    cocok: (p) => p.startsWith("/inventory") || p.startsWith("/request-stok"),
    items: [
      BERANDA,
      { id: "opname", lbl: "Opname Scan", ikon: ScanLine, to: "/inventory?tab=stok&action=opname",
        mod: "inventory_opname", g: ["#FF9966", "#FF5E62"] },
      { id: "stok-gudang", lbl: "Stok", ikon: Boxes, to: "/inventory?tab=stok",
        mods: ["inventory_laporan", "inventory_gudang", "inventory_unit", "inventory_transfer", "inventory_opname"],
        g: ["#56CCF2", "#2F80ED"] },
      { id: "po", lbl: "Input", ikon: ClipboardList, to: "/inventory?tab=input",
        mod: "inventory_po", g: ["#80FF72", "#7EE8FA"] },
      { id: "produk", lbl: "Produk", ikon: Package, to: "/inventory?tab=produk",
        mod: "inventory_produk", g: ["#ffa9c6", "#f434e2"] },
    ],
  },
  {
    id: "keuangan",
    cocok: (p) => p.startsWith("/finance"),
    items: [
      BERANDA,
      { id: "catat", lbl: "Catat", ikon: PencilLine, to: "/finance/catat",
        mod: "finance_catat", g: ["#80FF72", "#7EE8FA"] },
      { id: "jurnal", lbl: "Jurnal", ikon: BookOpen, to: "/finance/jurnal",
        mod: "finance_laporan", g: ["#56CCF2", "#2F80ED"] },
      { id: "rekon", lbl: "Rekon", ikon: Landmark, to: "/finance/rekonsiliasi",
        mod: "finance_rekonsiliasi", g: ["#FF9966", "#FF5E62"] },
      { id: "laporan", lbl: "Laporan", ikon: BarChart3, to: "/finance/laporan",
        mod: "finance_laporan", g: ["#ffa9c6", "#f434e2"] },
    ],
  },
  {
    id: "marketing",
    cocok: (p) => p.startsWith("/marketing"),
    items: [
      BERANDA,
      { id: "campaign", lbl: "Campaign", ikon: Megaphone, to: "/marketing/campaigns",
        mod: "marketing_campaign", g: ["#FF9966", "#FF5E62"] },
      { id: "konten", lbl: "Konten", ikon: CalendarDays, to: "/marketing/content",
        mod: "marketing_content", g: ["#56CCF2", "#2F80ED"] },
      { id: "kol", lbl: "KOL", ikon: Users, to: "/marketing/kol",
        mod: "marketing_kol", g: ["#80FF72", "#7EE8FA"] },
      { id: "anggaran", lbl: "Anggaran", ikon: Wallet, to: "/marketing/budget",
        mod: "marketing_budget", g: ["#ffa9c6", "#f434e2"] },
    ],
  },
  {
    id: "utama",
    cocok: () => true,
    items: [
      BERANDA,
      { id: "nota", lbl: "Nota", ikon: ScrollText, to: "/input-nota",
        mod: "nota_sales", g: ["#56CCF2", "#2F80ED"] },
      // `mods` eksplisit WAJIB di samping modPrefix: superadmin/admin lolos lewat
      // role di can() dengan user.permissions KOSONG, sehingga jalur modPrefix
      // (yang membaca daftar izin) tidak pernah true untuk mereka.
      { id: "stok", lbl: "Stok", ikon: Boxes, to: "/inventory?tab=stok",
        mods: ["inventory_laporan", "inventory_gudang", "inventory_unit", "inventory_transfer",
               "inventory_opname", "inventory_produk", "inventory_po"],
        modPrefix: "inventory_", g: ["#FF9966", "#FF5E62"] },
      { id: "keuangan", lbl: "Keuangan", ikon: CreditCard, to: "/finance",
        mods: ["finance", "finance_dashboard"], modPrefix: "finance_",
        g: ["#80FF72", "#7EE8FA"] },
      { id: "menu", lbl: "Menu", ikon: MoreHorizontal, aksi: "buka-menu",
        g: ["#ffa9c6", "#f434e2"] },
    ],
  },
];

export default function GradientNavHp() {
  const { user, can } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const path = location.pathname;

  // Logika saring izin DISAMAKAN dengan sidebar Layout.jsx — satu kebenaran.
  const boleh = useMemo(() => (it) => {
    if (it.modPrefix && Object.keys(user?.permissions || {})
      .some((k) => k.startsWith(it.modPrefix) && can(k, "view"))) return true;
    if (Array.isArray(it.mods)) return it.mods.some((m) => can(m, "view"));
    return !it.mod && !it.mods && !it.modPrefix ? true : (it.mod ? can(it.mod, "view") : false);
  }, [user, can]);

  const konteks = KONTEKS.find((k) => k.cocok(path)) || KONTEKS[KONTEKS.length - 1];
  const items = konteks.items.filter(boleh);

  // Item aktif = rute dengan awalan terpanjang yang cocok (Beranda hanya persis "/").
  const aktifId = useMemo(() => {
    let terbaik = null, panjang = -1;
    for (const it of items) {
      if (!it.to) continue;
      const [target, query] = it.to.split("?");
      const expected = new URLSearchParams(query);
      const current = new URLSearchParams(location.search);
      const pas = target === "/" ? path === "/" : path.startsWith(target) && [...expected].every(([key,value]) => current.get(key) === value);
      if (pas && it.to.length > panjang) { terbaik = it.id; panjang = it.to.length; }
    }
    return terbaik;
  }, [items, path, location.search]);

  // Kurang dari 2 tujuan = nav tidak berguna (mis. role sangat terbatas) — sembunyikan.
  if (items.length < 2) return null;

  const ketuk = (it) => {
    if (it.aksi === "buka-menu") {
      // Ditangkap Layout.jsx untuk membuka drawer menu lengkap yang sudah ada.
      window.dispatchEvent(new CustomEvent("epplehub:buka-menu"));
      return;
    }
    if (it.to) nav(it.to);
  };

  return <>
    <div className="h-24 md:hidden print:hidden" aria-hidden="true" />
    <div className="eh-mobile-dock">
      <nav aria-label="Navigasi utama" data-testid="navhp" style={{"--nav-count":items.length,"--nav-index":Math.max(0,items.findIndex(it=>it.id===aktifId))}}>
        <span className="eh-nav-indicator" style={{opacity:aktifId?1:0}} aria-hidden="true" />
        {items.map(it => {const Ikon=it.ikon;return <button type="button" key={it.id} data-testid={`navhp-${it.id}`} aria-label={it.lbl} aria-current={it.id===aktifId?"page":undefined} className={it.id===aktifId?"active":""} onClick={()=>ketuk(it)}><Ikon size={20}/><span>{it.lbl}</span></button>;})}
      </nav>
    </div>
  </>;
}
